import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface TourStep {
  /** Unique ID for this step */
  id: string;
  /** CSS selector for the target element to highlight */
  target: string;
  /** Title shown in the tooltip */
  title: string;
  /** Description/content for the step */
  content: string;
  /** Optional tip shown in a highlighted box */
  tip?: string;
  /** Placement of the tooltip relative to target */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional action to perform when this step is reached */
  onStepEnter?: () => void;
}

export interface Tour {
  /** Unique ID for this tour */
  id: string;
  /** Tour name for display */
  name: string;
  /** Steps in the tour */
  steps: TourStep[];
}

interface TourContextValue {
  /** Currently active tour */
  activeTour: Tour | null;
  /** Current step index in the active tour */
  currentStepIndex: number;
  /** Whether a tour is currently running */
  isTourActive: boolean;
  /** Start a tour by ID */
  startTour: (tourId: string) => void;
  /** End the current tour */
  endTour: () => void;
  /** Go to the next step */
  nextStep: () => void;
  /** Go to the previous step */
  prevStep: () => void;
  /** Skip the tour (mark as seen) */
  skipTour: () => void;
  /** Check if a tour has been completed */
  isTourCompleted: (tourId: string) => boolean;
  /** Reset a tour (allow it to show again) */
  resetTour: (tourId: string) => void;
  /** Reset all tours */
  resetAllTours: () => void;
  /** Register a tour definition */
  registerTour: (tour: Tour) => void;
  /** Check if a tour should auto-start */
  shouldAutoStart: (tourId: string, triggerCondition: boolean) => boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

const COMPLETED_TOURS_KEY = 'completedTours';

export function TourProvider({ children }: { children: ReactNode }) {
  const [tours, setTours] = useState<Map<string, Tour>>(new Map());
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());

  // Load completed tours from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(COMPLETED_TOURS_KEY);
    if (saved) {
      try {
        setCompletedTours(new Set(JSON.parse(saved)));
      } catch {
        // Ignore invalid JSON
      }
    }
  }, []);

  // Save completed tours to localStorage
  const saveCompletedTours = useCallback((tourIds: Set<string>) => {
    localStorage.setItem(COMPLETED_TOURS_KEY, JSON.stringify([...tourIds]));
    setCompletedTours(tourIds);
  }, []);

  const registerTour = useCallback((tour: Tour) => {
    setTours((prev) => {
      const next = new Map(prev);
      next.set(tour.id, tour);
      return next;
    });
  }, []);

  const startTour = useCallback((tourId: string) => {
    const tour = tours.get(tourId);
    if (tour && tour.steps.length > 0) {
      setActiveTour(tour);
      setCurrentStepIndex(0);
      // Call onStepEnter for the first step
      tour.steps[0]?.onStepEnter?.();
    }
  }, [tours]);

  const endTour = useCallback(() => {
    if (activeTour) {
      // Mark as completed
      const newCompleted = new Set(completedTours);
      newCompleted.add(activeTour.id);
      saveCompletedTours(newCompleted);
    }
    setActiveTour(null);
    setCurrentStepIndex(0);
  }, [activeTour, completedTours, saveCompletedTours]);

  const skipTour = useCallback(() => {
    if (activeTour) {
      // Mark as completed (skipped counts as completed)
      const newCompleted = new Set(completedTours);
      newCompleted.add(activeTour.id);
      saveCompletedTours(newCompleted);
    }
    setActiveTour(null);
    setCurrentStepIndex(0);
  }, [activeTour, completedTours, saveCompletedTours]);

  const nextStep = useCallback(() => {
    if (!activeTour) return;

    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= activeTour.steps.length) {
      // Tour complete
      endTour();
    } else {
      setCurrentStepIndex(nextIndex);
      // Call onStepEnter for the new step
      activeTour.steps[nextIndex]?.onStepEnter?.();
    }
  }, [activeTour, currentStepIndex, endTour]);

  const prevStep = useCallback(() => {
    if (!activeTour) return;

    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStepIndex(prevIndex);
      activeTour.steps[prevIndex]?.onStepEnter?.();
    }
  }, [activeTour, currentStepIndex]);

  const isTourCompleted = useCallback((tourId: string) => {
    return completedTours.has(tourId);
  }, [completedTours]);

  const resetTour = useCallback((tourId: string) => {
    const newCompleted = new Set(completedTours);
    newCompleted.delete(tourId);
    saveCompletedTours(newCompleted);
  }, [completedTours, saveCompletedTours]);

  const resetAllTours = useCallback(() => {
    saveCompletedTours(new Set());
  }, [saveCompletedTours]);

  const shouldAutoStart = useCallback((tourId: string, triggerCondition: boolean) => {
    // Only auto-start if:
    // 1. The trigger condition is met
    // 2. The tour hasn't been completed
    // 3. No other tour is currently active
    return triggerCondition && !completedTours.has(tourId) && !activeTour;
  }, [completedTours, activeTour]);

  const value: TourContextValue = {
    activeTour,
    currentStepIndex,
    isTourActive: activeTour !== null,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour,
    isTourCompleted,
    resetTour,
    resetAllTours,
    registerTour,
    shouldAutoStart,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

export default TourContext;
